import { forwardRef } from 'react';
import type { JSX } from 'react';

import { useNow } from '@change-corgi/core/react/date';
import { useI18n } from '@change-corgi/core/react/i18n';
import { Link } from '@change-corgi/core/react/router';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { linkResetStyles } from '@change-corgi/design-system-legacy/reset';
import { Text } from '@change-corgi/design-system-legacy/typography';

import type { PetitionContentUpdate } from 'src/app/pages/petitionGamma/details/shared/types';

import { UpdateBody } from './UpdateBody';
import { UpdateCardHeading } from './UpdateCardHeading';
import { UpdateMedia } from './UpdateMedia';

type Props = Readonly<{
	update: PetitionContentUpdate;
}>;

export const UpdateCard = forwardRef<HTMLAnchorElement, Props>(function UpdateCard(
	{ update }: Props,
	ref,
): JSX.Element | null {
	const now = useNow();
	const { localizeRelativeTime } = useI18n();
	return (
		<Link
			to={update.url}
			sx={{
				...linkResetStyles,
				display: 'block',
				borderRadius: 'standard',
				'&:focus, &:hover': {
					a: {
						color: 'primary-black',
						textDecoration: 'none',
					},
				},
			}}
			ref={ref}
			data-qa={`petition-update-${update.type}`}
		>
			<Box
				variant="bordered"
				sx={{
					borderColor: 'neutral-grey400',
					overflow: 'hidden',
				}}
				backgroundColor="primary-white"
			>
				<UpdateCardHeading update={update} />
				<Flex
					sx={{
						p: 16,
						gap: 16,
					}}
				>
					<UpdateMedia update={update} />

					<Box
						sx={{
							flex: 1,
						}}
					>
						<Text
							as="div"
							fontWeight="bold"
							mb={4}
							sx={{
								fontSize: [16, 24],
								lineHeight: 1.25,
							}}
						>
							{update.title}
						</Text>
						<Text as="div" color="typography-secondary" mb={4}>
							{localizeRelativeTime(now, update.createdAt)}
						</Text>
						<UpdateBody update={update} />
					</Box>
				</Flex>
			</Box>
		</Link>
	);
});
